import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Building2, Briefcase, MessageSquare, ChevronDown, ChevronUp, Edit3, Sparkles, Gift, Phone, Shield, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TestOptionsProps {
  agentConfig: any;
  sessionId?: string;
  agentId?: string;
  onRegister: (sessionId: string, agentId: string) => void;
  onBack: () => void;
}

const TestOptions = ({ agentConfig, sessionId: existingSessionId, agentId: existingAgentId, onRegister, onBack }: TestOptionsProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const [showKnowledge, setShowKnowledge] = useState(true);
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Extract business info for display
  const businessInfo = agentConfig.businessInfo || {};
  const businessName = businessInfo.name || agentConfig.businessName || agentConfig.name || t('testOptions.notSpecified');
  const businessType = businessInfo.type || agentConfig.purpose || agentConfig.type || '';
  const services = businessInfo.services || agentConfig.services || agentConfig.features || [];
  const capabilities = agentConfig.capabilities || [];
  const knowledgeBase = agentConfig.knowledgeBase || '';
  
  // Extract key info from conversation for display if no structured data
  const hasStructuredData = capabilities.length > 0 || services.length > 0 || knowledgeBase;

  const handleSignup = async () => {
    if (isSigningUp) return;
    setIsSigningUp(true);

    try {
      let sid = existingSessionId || '';
      let aid = existingAgentId || '';

      // Only create agent if we don't have sessionId/agentId already
      if (!sid || !aid) {
        // Create an intro agent on Call24x7 and receive the real session_id/agent_id
        const { data, error } = await supabase.functions.invoke('intro-agent', {
          body: { agentConfig },
        });

        if (error || !data?.success) {
          console.error('Error creating intro agent:', error || data);
          toast.error('Failed to prepare your agent. Please try again.');
          setIsSigningUp(false);
          return;
        }

        sid = data.sessionId as string;
        aid = data.agentId as string;
      }

      // Keep parent flow compatible (some pages may still use the register step)
      onRegister(sid, aid);

      // Set cookie (works across subdomains only when hosted on call24x7.ai)
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + (24 * 60 * 60 * 1000));
      const cookieDomain = window.location.hostname.endsWith('call24x7.ai') ? '; domain=.call24x7.ai' : '';
      document.cookie = `session_id=${sid}${cookieDomain}; expires=${expirationDate.toUTCString()}; path=/; secure; SameSite=None`;

      // Redirect to signup (include session_id so it also works from preview domains)
      const signupUrl = new URL('https://manage.call24x7.ai/signup');
      signupUrl.searchParams.set('session_id', sid);
      signupUrl.searchParams.set('agent_id', aid);
      window.location.href = signupUrl.toString();
    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsSigningUp(false);
    }
  };

  return (
    <div className={`max-w-4xl mx-auto animate-fade-in ${isRTL ? 'rtl' : ''}`}>
      <div className={`mb-6 flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
          {t('testOptions.back')}
        </Button>
        <h2 className="text-3xl font-bold text-foreground">{t('testOptions.title')}</h2>
      </div>

      {/* Signup CTA - Top */}
      <Card 
        className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30 p-6 mb-6"
      >
        <div className={`flex flex-col md:flex-row items-center gap-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Gift className="h-8 w-8 text-primary" />
          </div>
          <div className={`flex-1 text-center md:text-left ${isRTL ? 'md:text-right' : ''}`}>
            <h3 className="font-bold text-xl text-foreground mb-2">
              {t('testOptions.signupToTest', 'Sign up to test your agent')}
            </h3>
            <p className="text-muted-foreground mb-3">
              {t('testOptions.signupBenefit', 'Get $5 free credits when you sign up. Make test calls, edit your agent, and build unlimited agents.')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <span className="text-sm text-primary flex items-center gap-1">
                <Phone className="h-4 w-4" />
                {t('testOptions.freeTestCalls', 'Free test calls')}
              </span>
              <span className="text-sm text-primary flex items-center gap-1">
                <Edit3 className="h-4 w-4" />
                {t('testOptions.editAnytime', 'Edit anytime')}
              </span>
              <span className="text-sm text-primary flex items-center gap-1">
                <Shield className="h-4 w-4" />
                {t('testOptions.noCommitment', 'No commitment')}
              </span>
            </div>
          </div>
          <Button 
            onClick={handleSignup}
            size="lg"
            disabled={isSigningUp}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
          >
            {isSigningUp ? (
              <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
            ) : (
              <Sparkles className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            )}
            {isSigningUp ? t('testOptions.saving', 'Saving...') : t('testOptions.signupButton')}
          </Button>
        </div>
      </Card>

      {/* Agent Knowledge Preview */}
      <Card className="bg-card/80 backdrop-blur-sm border-border shadow-xl mb-6 overflow-hidden">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => setShowKnowledge(!showKnowledge)}
        >
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <h3 className="font-semibold text-foreground">{t('testOptions.agentKnowledge')}</h3>
              <p className="text-sm text-muted-foreground">{t('testOptions.reviewBeforeTest')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onBack(); }}
              className="text-primary hover:text-primary/80"
            >
              <Edit3 className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {t('testOptions.editInChat')}
            </Button>
            {showKnowledge ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
          </div>
        </div>
        
        {showKnowledge && (
          <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
            {/* Business Info */}
            <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
              <Building2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{t('testOptions.businessInfo')}</p>
                <p className="text-sm text-muted-foreground">{businessName}</p>
                {businessType && <p className="text-xs text-muted-foreground/70">{businessType}</p>}
              </div>
            </div>

            {/* Services / Features */}
            {services.length > 0 && (
              <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <Briefcase className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t('testOptions.services')}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {services.slice(0, 5).map((service: string, idx: number) => (
                      <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {typeof service === 'string' ? service.replace(/-/g, ' ') : service}
                      </span>
                    ))}
                    {services.length > 5 && (
                      <span className="text-xs text-muted-foreground">+{services.length - 5} {t('testOptions.more')}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Agent Settings Summary */}
            {!hasStructuredData && (
              <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <Briefcase className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t('testOptions.agentSettings')}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {agentConfig.purpose && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {agentConfig.purpose}
                      </span>
                    )}
                    {agentConfig.language && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {agentConfig.language}
                      </span>
                    )}
                    {agentConfig.tone && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {agentConfig.tone}
                      </span>
                    )}
                    {agentConfig.voiceGender && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {agentConfig.voiceGender} voice
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Capabilities */}
            {capabilities.length > 0 && (
              <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <MessageSquare className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{t('testOptions.capabilities')}</p>
                  <ul className={`text-sm text-muted-foreground mt-1 space-y-0.5 ${isRTL ? 'pr-4' : 'pl-4'}`}>
                    {capabilities.slice(0, 4).map((cap: string, idx: number) => (
                      <li key={idx} className="list-disc">{cap}</li>
                    ))}
                    {capabilities.length > 4 && (
                      <li className="list-none text-xs text-muted-foreground/70">+{capabilities.length - 4} {t('testOptions.more')}</li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Knowledge Summary */}
            {knowledgeBase && (
              <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <div className="h-5 w-5 flex-shrink-0" /> {/* Spacer for alignment */}
                <div className="flex-1 bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground line-clamp-3">{knowledgeBase.substring(0, 200)}...</p>
                </div>
              </div>
            )}

            <p className={`text-xs text-muted-foreground/70 pt-2 ${isRTL ? 'text-right' : ''}`}>
              {t('testOptions.editHint')}
            </p>
          </div>
        )}
      </Card>

      {/* Agent Summary Card */}
      <Card className="bg-card/80 backdrop-blur-sm border-border shadow-2xl p-8">
        <div className="space-y-6">
          <div className={`text-center space-y-2 ${isRTL ? 'text-right' : ''}`}>
            <h3 className="text-2xl font-bold text-foreground">
              {agentConfig.name}
            </h3>
            <p className="text-muted-foreground">
              {agentConfig.description || t('testOptions.customAgent')}
            </p>
          </div>

          <div className="space-y-6 pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground">
                {t('testOptions.agentReady', 'Your agent is ready!')}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {t('testOptions.signupPromptNew', 'Sign up now to save your agent configuration, make test calls with your $5 free credit, and start handling real calls.')}
              </p>
            </div>

            <Button
              onClick={handleSignup}
              disabled={isSigningUp}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg rounded-xl"
            >
              {isSigningUp ? (
                <Loader2 className={`h-5 w-5 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
              ) : (
                <Gift className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              )}
              {isSigningUp ? t('testOptions.saving', 'Saving agent...') : t('testOptions.signupGetCredits', 'Sign Up & Get $5 Free Credits')}
            </Button>

            <p className={`text-center text-xs text-muted-foreground ${isRTL ? 'text-right' : ''}`}>
              {t('testOptions.settingsPreserved')}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TestOptions;
